import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from 'config-hub-server/src/router/index.js';
import { countWord } from './pluralise.js';

/** The asset types, inferred off `AppRouter` — the server is the one place they are declared. */
type AssetOutputs = inferRouterOutputs<AppRouter>['assets'];
/** A kind as `assets.status` sends it: the backoffice's registry, read through the hub. */
export type AssetKindView = AssetOutputs['status']['kinds'][number];
export type AssetKind = AssetKindView['name'];
/** One row of `assets.list` — a game's file of one kind. */
export type AssetEntry = AssetOutputs['list'][number];
/** Where a file is still used (`assets.references`). */
export type AssetReference = AssetOutputs['references'][number];
/** A game, as the asset procedures name it. */
export type GameRef = { studio: string; id: string };

const accepts = (kind: AssetKindView, type: string, name: string): boolean =>
    kind.accept.includes(type) || kind.extensions.some((ext) => name.toLowerCase().endsWith(ext));

/** One file's size limit, always in MB — the backoffice's `validateAsset` never says GB. */
export const maxSizeLabel = (maxBytes: number): string => `${Math.round(maxBytes / 1024 / 1024)} MB`;

/**
 * A copy of the backoffice's `validateAsset` (`common/assetKinds.ts`), checked before a byte is
 * sent: the client cannot import `@backoffice/*` at runtime. `assets.test.ts` pins the copy
 * against the real one, wording included.
 */
export const fileProblem = (kind: AssetKindView, file: { name: string; type: string; size: number }): string | null => {
    if (!accepts(kind, file.type, file.name))
        return `Unsupported file type for ${kind.label}: ${file.type || 'unknown'}`;
    if (file.size > kind.maxBytes) return `${kind.label} exceeds the ${maxSizeLabel(kind.maxBytes)} limit`;
    return null;
};

export const acceptAttr = (kind: AssetKindView): string => [...kind.accept, ...kind.extensions].join(',');

export const pluralKind = (kind: AssetKindView, count: number): string => countWord(count, kind.label.toLowerCase());
